/**
 * Die Fassade in der Ansicht (Kapitel G).
 *
 * Die Fassade ist im Luftbild nicht zu sehen — sichtbar ist nur ihre
 * Grundlinie. Belegt wird deshalb hier, in der aufgespannten Ansicht: Wand von
 * vorne, Fenster und Tueren als Aussparungen, Module dort, wo Platz ist.
 *
 * Die Rechnung dahinter ist dieselbe wie beim Dach — nur senkrecht.
 */

import { useRef, useState } from 'react';

import type { Flaeche } from '../typen.ts';
import type { Planung } from '../planung.ts';
import { platzSchluessel } from '../planung.ts';

interface Eigenschaften {
  flaeche: Flaeche;
  planung: Planung | null;
  onFlaeche: (felder: Partial<Flaeche>) => void;
  onModulGetippt: (schluessel: string) => void;
}

export function Fassadenansicht(e: Eigenschaften): React.JSX.Element {
  const { flaeche, planung } = e;
  const bild = useRef<SVGSVGElement>(null);
  const [zeichnet, setZeichnet] = useState(false);
  const [ecke, setEcke] = useState<{ u: number; v: number } | null>(null);

  if (!flaeche.fassade || !planung?.ansicht) {
    return <p className="gedaempft">Für diese Fläche gibt es keine Ansicht.</p>;
  }

  const breite = planung.ansicht.breiteM;
  const hoehe = flaeche.fassade.hoeheM + (flaeche.fassade.sockelhoeheM ?? 0);
  const rand = 0.4;

  /** Bildkoordinaten -> Meter in der Ansicht. Oben ist oben, anders als im SVG. */
  function ausBild(ereignis: React.MouseEvent<SVGSVGElement>): { u: number; v: number } | null {
    const flaecheImBild = bild.current?.getBoundingClientRect();
    if (!flaecheImBild) return null;
    const u =
      ((ereignis.clientX - flaecheImBild.left) / flaecheImBild.width) * (breite + 2 * rand) - rand;
    const v =
      hoehe +
      rand -
      ((ereignis.clientY - flaecheImBild.top) / flaecheImBild.height) * (hoehe + 2 * rand);
    return { u, v };
  }

  function beiKlick(ereignis: React.MouseEvent<SVGSVGElement>): void {
    if (!zeichnet) return;
    const punkt = ausBild(ereignis);
    if (!punkt) return;
    if (!ecke) {
      setEcke(punkt);
      return;
    }
    // Zweiter Klick schliesst das Rechteck ab. Aussparungen der Fassade werden
    // in der Ansicht gesetzt, nicht im Luftbild (PV-G04).
    const neu = [
      { lng: Math.min(ecke.u, punkt.u), lat: Math.min(ecke.v, punkt.v) },
      { lng: Math.max(ecke.u, punkt.u), lat: Math.min(ecke.v, punkt.v) },
      { lng: Math.max(ecke.u, punkt.u), lat: Math.max(ecke.v, punkt.v) },
      { lng: Math.min(ecke.u, punkt.u), lat: Math.max(ecke.v, punkt.v) },
    ];
    e.onFlaeche({ aussparungen: [...flaeche.aussparungen, neu] });
    setEcke(null);
    setZeichnet(false);
  }

  return (
    <div>
      <div className="knopfreihe">
        <button aria-pressed={zeichnet} onClick={() => setZeichnet(!zeichnet)}>
          {zeichnet ? (ecke ? 'Zweite Ecke setzen' : 'Erste Ecke setzen') : 'Fenster aussparen'}
        </button>
        {flaeche.aussparungen.length > 0 && (
          <button
            onClick={() => {
              if (confirm(`Alle ${e.flaeche.aussparungen.length} Hindernisse dieser Fassade löschen?`)) e.onFlaeche({ aussparungen: [] });
            }}
          >
            Hindernisse löschen
          </button>
        )}
      </div>

      <svg
        ref={bild}
        viewBox={`${-rand} ${-rand} ${breite + 2 * rand} ${hoehe + 2 * rand}`}
        style={{
          width: '100%',
          height: 'auto',
          border: '1px solid var(--linie)',
          background: 'var(--flaeche)',
          cursor: zeichnet ? 'crosshair' : 'default',
        }}
        onClick={beiKlick}
      >
        {/* Von unten nach oben gedacht, deshalb gespiegelt gezeichnet. */}
        <g transform={`translate(0, ${hoehe}) scale(1, -1)`}>
          <rect
            x={0}
            y={0}
            width={breite}
            height={hoehe}
            fill="#ffffff"
            stroke="#000000"
            strokeWidth={0.05}
          />

          {flaeche.aussparungen.map((ring, index) => {
            const us = ring.map((p) => p.lng);
            const vs = ring.map((p) => p.lat);
            return (
              <rect
                key={index}
                x={Math.min(...us)}
                y={Math.min(...vs)}
                width={Math.max(...us) - Math.min(...us)}
                height={Math.max(...vs) - Math.min(...vs)}
                fill="#d86038"
                fillOpacity={0.35}
                stroke="#d86038"
                strokeWidth={0.04}
              />
            );
          })}

          {planung.ansicht.module.map((platz) => {
            const us = platz.ecken.map((p) => p.x);
            const vs = platz.ecken.map((p) => p.y);
            return (
              <rect
                key={platzSchluessel(platz)}
                x={Math.min(...us)}
                y={Math.min(...vs)}
                width={Math.max(...us) - Math.min(...us)}
                height={Math.max(...vs) - Math.min(...vs)}
                fill="#1f3a5f"
                stroke="#ffffff"
                strokeWidth={0.02}
                style={{ cursor: 'pointer' }}
                onClick={(ereignis) => {
                  ereignis.stopPropagation();
                  e.onModulGetippt(platzSchluessel(platz));
                }}
              />
            );
          })}
        </g>
      </svg>

      <p style={{ fontSize: '0.78rem', color: 'var(--grau)', marginTop: '0.4rem' }}>
        {breite.toFixed(2)} m breit, {flaeche.fassade.hoeheM.toFixed(2)} m hoch ·{' '}
        {planung.ansicht.module.length} Module · Modul antippen entfernt es
      </p>
    </div>
  );
}
